import styled, { css } from 'styled-components';
import { RichText } from '@sitecore-jss/sitecore-jss-react';

import {
  mq,
  pageTitle,
  heading_xxl,
  heading_xl,
  heading_l,
  heading_m,
  heading_s,
  heading_xs,
  text_l,
  text_m,
  text_s,
  text_xs,
  text_xscap,
  link,
  unorderedList
} from '_utils/styles/';
import { spacing } from '_utils/props';

export const baseStyles = () => (
  css`
    word-break: break-word;
  `
);

export const blockStyle = ({ theme }) => css`
  p,
  ul,
  ol,
  dl,
  blockquote,
  table {
    ${spacing({ theme, $my: 2 })}

    &:first-child {
      ${spacing({ theme, $mt: 0 })}
    }

    &:last-child {
      ${spacing({ theme, $mb: 0 })}
    }
  }
`;

export const headingMargin = ({ theme }) => css`
  ${spacing({ theme, $mt: 5 })};

  &:first-child {
    ${spacing({ theme, $mt: 0 })};
  }
`;

export const headingStyle = ({ theme }) => css`
  .pageTitle {
    ${pageTitle};
  }
  .heading-xxl,
  h1 {
    ${heading_xxl};
    ${headingMargin}
  }

  .heading-xl,
  h2 {
    ${heading_xl};
    ${headingMargin}
  }

  .heading-l,
  h3 {
    ${heading_l};
    ${headingMargin}
  }

  .heading-m,
  h4 {
    ${heading_m};
    ${headingMargin}
  }

  .heading-s,
  h5 {
    ${heading_s};
    ${headingMargin}
  }

  .heading-xs,
  h6 {
    ${heading_xs};
    ${headingMargin}
  }

  /* reduce margin top for headings that follow immediately after the next size up */
  h1 + h2,
  h2 + h3,
  h3 + h4,
  h4 + h5,
  h5 + h6,
  .heading-xxl + .heading-xl,
  .heading-xl + .heading-l,
  .heading-l + .heading-m,
  .heading-m + .heading-s,
  .heading-s + .heading-xs {
    ${spacing({ theme, $mt: 2 })}
  }
`;

export const textStyle = () => css`
  .text-l {
    ${text_l}
  }

  .text-m {
    ${text_m}
  }

  .text-s {
    ${text_s}
  }

  .text-xs {
    ${text_xs}
  }

  .text-xscap {
    ${text_xscap}
  }
`;

export const linkStyle = () => css`
  .link,
  a {
    ${link}
  }
`;

export const calloutStyle = ({ theme }) => css`
  .callout {
    display: block;
    ${spacing({
  theme,
  $p: 2,
  $pl: {
    xs: theme.content.indent.xs + 'px',
    md: theme.content.indent.md + 'px'
  }
})}
    position: relative;

    > * {
      &:first-child {
        margin-top: 0;
      }
      &:last-child {
        margin-bottom: 0;
      }
    }

    &:before {
      ${spacing({ theme, $mt: 2 })}
      position: absolute;
      display: block;
      content: '';
      background-color: ${theme.colors.element.secondary};
      width: 4px;
      height: calc(100% - 32px);
      top: 0;
      left: 12px;
    }
  }
`;

export const blockquoteStyle = ({ theme }) => css`
  blockquote {
    ${spacing({
  theme,
  $m: 2,
  $ml: {
    xs: theme.content.indent.xs + 'px',
    md: theme.content.indent.md + 'px'
  }
})}
  }
`;

// customise tooltip styling TBC
export const tooltipStyle = ({ theme }) => css`
  span[title],
  a[title] {
    display: inline-block;
    border-bottom: 2px dotted ${theme.colors.element.primary};
  }
`;

export const buttonContainerStyle = ({ theme }) => css`
  .button-container {
    display: flex;
    flex-direction: column;

    > * {
      ${spacing({ theme, $mt: { xs: 2, sm: 0 }, $ml: { xs: 0, sm: 2 } })}

      &:first-child {
        ${spacing({ theme, $mt: { xs: 0 }, $ml: { sm: 0 } })}
      }
    }

    ${mq('sm')} {
      flex-direction: row;
      align-items: center;
    }
  }
`;

export const listStyle = ({ theme }) => css`
  /* 
   * unordered list 
   */

  ul {
    ${unorderedList({ theme })}
  }

  /*
   * nesting
   */
  ol,
  ul {
    ol,
    ul {
      &:first-child {
        ${spacing({ theme, $mt: 1 })}
      }
      &:last-child {
        ${spacing({ theme, $mb: 1 })}
      }
    }
  }

  /* nested ul */
  ol,
  ul {
    ul li::before {
      content: '-';
      font-size: 1.5rem;
      font-family: 'sans-serif';
    }

    p {
      ${spacing({ theme, $my: 1 })}
    }
  }

  /* 
   * ordered list 
   */

  ol {
    padding: 0 0 0 32px;
    margin: 16px 0;

    > li {
      padding: 4px 0 4px 16px;
    }

    &.featured {
      list-style: none;
      padding: 0;
      counter-reset: section;

      ol.featured {
        list-style: none;
        padding-left: 44px;
        counter-reset: section;
      }

      > li {
        padding: 4px 0 4px 44px;
        text-indent: 0;
        position: relative;

        ol.featured li {
          padding: 4px 0 4px 0px;
        }
      }

      > li::before {
        position: absolute;
        counter-increment: section;
        content: counters(section, '.');
        font-weight: bold;
        color: ${theme.colors.element.secondary};
        line-height: 24px;
        display: inline-block;
        border: 2px solid ${theme.colors.element.secondary};
        min-width: 28px;
        font-size: 14px;
        height: 28px;
        border-radius: 999px;
        text-align: center;
        text-indent: 0;
        left: 4px;
      }

      > li ol.featured li::before {
        font-size: 12px;
        left: -40px;
        letter-spacing: -0.5px;
      }
    }
  }
  /* temp fix for numbered lists  */
  ol.featured[start='1'] {
    counter-reset: section 0;
  }
  ol.featured[start='2'] {
    counter-reset: section 1;
  }
  ol.featured[start='3'] {
    counter-reset: section 2;
  }
  ol.featured[start='4'] {
    counter-reset: section 3;
  }
  ol.featured[start='5'] {
    counter-reset: section 4;
  }
  ol.featured[start='6'] {
    counter-reset: section 5;
  }
  ol.featured[start='7'] {
    counter-reset: section 6;
  }
  ol.featured[start='8'] {
    counter-reset: section 7;
  }
  ol.featured[start='9'] {
    counter-reset: section 8;
  }
  ol.featured[start='10'] {
    counter-reset: section 9;
  }
  ol.featured[start='11'] {
    counter-reset: section 10;
  }
  ol.featured[start='12'] {
    counter-reset: section 11;
  }
  ol.featured[start='13'] {
    counter-reset: section 12;
  }
  ol.featured[start='14'] {
    counter-reset: section 13;
  }
  ol.featured[start='15'] {
    counter-reset: section 14;
  }
  ol.featured[start='16'] {
    counter-reset: section 15;
  }
  ol.featured[start='17'] {
    counter-reset: section 16;
  }
  ol.featured[start='18'] {
    counter-reset: section 17;
  }
  ol.featured[start='19'] {
    counter-reset: section 18;
  }
  ol.featured[start='20'] {
    counter-reset: section 19;
  }

  ol.nested-counter {
    list-style: none;
    padding: 0;
    counter-reset: section;

    li {
      position: relative;
      padding: 4px 0 4px 48px;
    }

    li::before {
      display: inline-block;
      position: absolute;
      content: counters(section, '.') '. ';
      counter-increment: section;
      left: 16px;
    }

    ol.nested-counter {
      li::before {
        text-align: right;
        content: counters(section, '.') ' ';
        left: 28px;
        transform: translateX(-100%);
      }
    }
  }

  /* definition list */
  dl {
  }

  dt {
    font-weight: bold;
  }

  dd {
    margin: 0;

    ${spacing({
  theme,
  $p: 0,
  $pl: {
    xs: theme.content.indent.xs + 'px',
    md: theme.content.indent.md + 'px'
  }
})}

    p:first-of-type {
      margin-top: 0;
    }

    p:last-of-type {
      margin-bottom: 0;
    }
  }
`;

export const tableStyle = ({ theme }) => css`
  .table-scroll-container {
    width: 100%;
    overflow-x: auto;

    table {
      min-width: 540px;
    }
  }

  table {
    border-collapse: collapse;
  }

  thead {
    background-color: ${theme.colors.table.head.background};
    color: ${theme.colors.table.head.color};
  }

  tbody {
    tr {
      background-color: ${theme.colors.table.row.background};

      &:nth-child(2n) {
        background-color: ${theme.colors.table.row.backgroundAlt};
      }
    }
  }

  td,
  th {
    font-size: ${theme.table.font.fontSize.sm}px;
    line-height: ${theme.table.font.lineHeight.sm}px;
    border: 1px solid ${theme.colors.table.cell.border};
    text-align: left;
    vertical-align: top;

    ${mq('md')} {
      font-size: ${theme.table.font.fontSize.md}px;
      line-height: ${theme.table.font.lineHeight.md}px;
    }

    ${spacing({ theme, $p: 2 })}
  }
`;

export const responsiveEmbedStyle = () => css`
  .responsive-embed {
    position: relative;
    height: 0;
    margin-bottom: 1rem;
    padding-bottom: 75%;
    overflow: hidden;
  }
  .responsive-embed iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  .responsive-embed.widescreen {
    padding-bottom: 56.25%;
  }
`;

export const accordionStyles = () => css`
  .accordion-content-container {
    display: flex;
    ${mq.lessThan('md')}{
        flex-direction: column;
        align-items: flex-start;
        margin-right: 16px;
    }
    &:not(:first-child) {
        ${({ theme }) => spacing({ theme, $mt: 2 })};
    }
  }
  
  .accordion-icon {
    display: flex;
    align-items: flex-start;
    ${({ theme }) => spacing({ theme, $mr: 2 })}
  }
  
  .accordion-icon-secondary {
    display: flex;
    align-items: flex-start;
    ${({ theme }) => spacing({ theme, $mr: 1 })}
  }
  
  .accordion-icon-pdf {
    width: 22px;
    height: 27px;
    background-image: url("data:image/svg+xml,%3Csvg%20width%3D%2216%22%20height%3D%2220%22%20viewBox%3D%220%200%2016%2020%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%20%20%20%20%3Cpath%0A%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22%23B00020%22%0A%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M4.62031%2012.8427H4.01587V11.9438H4.62031C4.75237%2011.9438%204.86412%2011.9831%204.94539%2012.0618C5.02666%2012.1404%205.07237%2012.2472%205.07237%2012.3933C5.07237%2012.5393%205.02666%2012.6517%204.94539%2012.7303C4.86412%2012.809%204.75237%2012.8427%204.62031%2012.8427Z%22%0A%20%20%20%20%2F%3E%0A%20%20%20%20%3Cpath%0A%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22%23B00020%22%0A%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M7.99685%2014.0562H7.45336V11.9438H7.99685C8.18478%2011.9438%208.34224%2011.9888%208.46415%2012.0674C8.58605%2012.1517%208.67748%2012.2697%208.73843%2012.4213C8.79938%2012.5787%208.83494%2012.7697%208.83494%2013C8.83494%2013.236%208.79938%2013.427%208.73843%2013.5787C8.67748%2013.736%208.58605%2013.8539%208.46415%2013.9326C8.34224%2014.0169%208.18478%2014.0562%207.99685%2014.0562Z%22%0A%20%20%20%20%2F%3E%0A%20%20%20%20%3Cpath%0A%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22%23B00020%22%0A%20%20%20%20%20%20%20%20%20%20%20%20fill-rule%3D%22evenodd%22%0A%20%20%20%20%20%20%20%20%20%20%20%20clip-rule%3D%22evenodd%22%0A%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M15.94%206.67C15.9695%206.75763%2015.9896%206.84813%2016%206.94V17C16%2017.7956%2015.6839%2018.5587%2015.1213%2019.1213C14.5587%2019.6839%2013.7956%2020%2013%2020H3C2.20435%2020%201.44129%2019.6839%200.87868%2019.1213C0.316071%2018.5587%200%2017.7956%200%2017V3C0%202.20435%200.316071%201.44129%200.87868%200.87868C1.44129%200.316071%202.20435%200%203%200H9.05C9.16622%200.0143442%209.27841%200.0517412%209.38%200.11C9.40985%200.10576%209.44015%200.10576%209.47%200.11C9.57282%200.158081%209.66734%200.222216%209.75%200.3L15.75%206.3C15.8278%206.38267%2015.8919%206.47718%2015.94%206.58V6.67ZM12.59%206L10%203.41V5C10%205.26522%2010.1054%205.51957%2010.2929%205.70711C10.4804%205.89464%2010.7348%206%2011%206H12.59ZM13.7071%2017.7071C13.8946%2017.5196%2014%2017.2652%2014%2017V16H2V17C2%2017.2652%202.10536%2017.5196%202.29289%2017.7071C2.48043%2017.8946%202.73478%2018%203%2018H13C13.2652%2018%2013.5196%2017.8946%2013.7071%2017.7071ZM2%2010V3C2%202.73478%202.10536%202.48043%202.29289%202.29289C2.48043%202.10536%202.73478%202%203%202H8V5C8%205.79565%208.31607%206.55871%208.87868%207.12132C9.44129%207.68393%2010.2044%208%2011%208H14V10H2ZM4.6965%2011H3V15H4.01587V13.7865H4.6965C5.12317%2013.7865%205.46348%2013.6629%205.71237%2013.4101C5.95618%2013.1573%206.08316%2012.8202%206.08316%2012.3933C6.08316%2011.9719%205.95618%2011.6348%205.71237%2011.382C5.46348%2011.1292%205.12317%2011%204.6965%2011ZM8.01716%2011H6.43749V15H8.01716C8.327%2015%208.60129%2014.9551%208.84002%2014.8596C9.07875%2014.764%209.27684%2014.6292%209.42922%2014.4494C9.5816%2014.2697%209.69843%2014.0618%209.77462%2013.8146C9.85081%2013.5674%209.89144%2013.2978%209.89144%2013C9.89144%2012.7079%209.85081%2012.4382%209.77462%2012.191C9.69843%2011.9438%209.5816%2011.736%209.42922%2011.5562C9.27684%2011.3764%209.07875%2011.2416%208.84002%2011.1461C8.60129%2011.0506%208.327%2011%208.01716%2011ZM13%2011V12H11.2629V12.5225H12.8883V13.4663H11.2629V15H10.247V11H13Z%22%0A%20%20%20%20%2F%3E%0A%3C%2Fsvg%3E");
    background-repeat: no-repeat;
    background-size: 22px 27px;
    ${mq.lessThan('md')}{
      width: 24px;
      height: 24px;
      background-size: 24px 24px;
      margin-top: 2px;
    }
  }
  
  .accordion-title-wrapper {
    display: flex;
  }
  
  .accordion-icon-download {
						  
    vertical-align: middle;
    ${({ theme }) => spacing({ theme, $ml: 1 })}
    margin-top: 4px;
    width: 16px;
    height: 18px;
    background-image: url("data:image/svg+xml,%3Csvg%20width%3D%2216%22%20height%3D%2218%22%20viewBox%3D%220%200%2016%2018%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%20%20%20%20%3Cpath%20fill%3D%22green%22%20d%3D%22M15%2016H1C0.734784%2016%200.48043%2016.1054%200.292893%2016.2929C0.105357%2016.4804%200%2016.7348%200%2017C0%2017.2652%200.105357%2017.5196%200.292893%2017.7071C0.48043%2017.8946%200.734784%2018%201%2018H15C15.2652%2018%2015.5196%2017.8946%2015.7071%2017.7071C15.8946%2017.5196%2016%2017.2652%2016%2017C16%2016.7348%2015.8946%2016.4804%2015.7071%2016.2929C15.5196%2016.1054%2015.2652%2016%2015%2016ZM7.29%2013.71C7.3851%2013.801%207.49725%2013.8724%207.62%2013.92C7.7397%2013.9729%207.86913%2014.0002%208%2014.0002C8.13087%2014.0002%208.2603%2013.9729%208.38%2013.92C8.50275%2013.8724%208.6149%2013.801%208.71%2013.71L13.71%208.71C13.8983%208.5217%2014.0041%208.2663%2014.0041%208C14.0041%207.7337%2013.8983%207.4783%2013.71%207.29C13.5217%207.1017%2013.2663%206.99591%2013%206.99591C12.7337%206.99591%2012.4783%207.1017%2012.29%207.29L9%2010.59V1C9%200.734784%208.89464%200.48043%208.70711%200.292893C8.51957%200.105357%208.26522%200%208%200C7.73478%200%207.48043%200.105357%207.29289%200.292893C7.10536%200.48043%207%200.734784%207%201V10.59L3.71%207.29C3.61676%207.19676%203.50607%207.1228%203.38425%207.07234C3.26243%207.02188%203.13186%206.99591%203%206.99591C2.86814%206.99591%202.73757%207.02188%202.61575%207.07234C2.49393%207.1228%202.38324%207.19676%202.29%207.29C2.19676%207.38324%202.1228%207.49393%202.07234%207.61575C2.02188%207.73757%201.99591%207.86814%201.99591%208C1.99591%208.13186%202.02188%208.26243%202.07234%208.38425C2.1228%208.50607%202.19676%208.61676%202.29%208.71L7.29%2013.71Z%22%0A%20%20%20%20%2F%3E%0A%3C%2Fsvg%3E");
    background-repeat: no-repeat;
    background-size: 16px 18px;
    background-position: center;
    ${mq.lessThan('md')}{
      width: 20px;
      height: 20px;
      background-size: 13px 15px;
      margin-top: 0px;
    }   
  }
  
  .accordion-title {
    display: flex;
    align-items: center;
    ${mq.lessThan('sm')}{
        align-items: flex-start;
     }
    ${heading_s}
    color: #041C2C;
    text-align: start;
    margin-right: 10px;
    box-shadow: none !important;
    &:hover{
        background-color: transparent !important;
        color: #2DB84B !important;
    }
  }
  
  .accordion-content-wrapper {
    display: flex; 
    flex-direction: column;
    ${mq.lessThan('md')}{
        margin-top: 10px;
    }
  }
  
  .accordion-description {
    ${text_s};
    text-align: start;
    ${({ theme }) => spacing({ theme, $m: 0 })}
    color: #4E5A5F;
  }
  
  .accordion-detail {
    display: flex;
    align-items: center;
    text-align: start;
    ${text_xs};
    color: #4E5A5F;
    ${({ theme }) => spacing({ theme, $my: 1 })}
    ${mq.lessThan('sm')}{
      flex-wrap: wrap;
    }
  }
  
  .accordion-version-container{
    display: flex;
    flex-direction: column;
					  
    ${({ theme }) => spacing({ theme, $p: 2, $mt: 3 })}
    border: 1px solid #DDDEE1;
    border-radius: 8px;
  }
  
  .accordion-version-item{
    display: flex;
    &:not(:last-child) {
        ${({ theme }) => spacing({ theme, $mb: 2 })};
    }
  }
  
  .accordion-version-wrapper {
    display: flex; 
    flex-direction: column;
  }
  
  .accordion-title-link{
    text-align: start;
    color: #041C2C;
    text-decoration: underline !important;
    box-shadow: none !important;
    font-size: 16px;
    line-height: 20px;
    &:hover{
       color: #2DB84B !important;
       background-color: transparent !important;
    }
    &:active{
       background-color: transparent !important;
    }
    &:focus{
       outline: none;
    }
    &:focus-within{
       color: #FFF;
       outline: 2px solid #8CA0FF;
    }
    ${mq.lessThan('md')}{
        font-size: 14px;
        line-height: 18px;
    }
  }
  
  .accordion-icon-pdf-version {
    width: 20px;
    height: 20px;
    background-image: url("data:image/svg+xml,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cpath%20d%3D%22M7.18363%2012.3689H6.67993V11.6199H7.18363C7.29369%2011.6199%207.38681%2011.6526%207.45453%2011.7182C7.52226%2011.7837%207.56035%2011.8727%207.56035%2011.9944C7.56035%2012.1161%207.52226%2012.2098%207.45453%2012.2753C7.38681%2012.3408%207.29369%2012.3689%207.18363%2012.3689Z%22%20fill%3D%22%23B00020%22%2F%3E%0A%3Cpath%20d%3D%22M9.99741%2013.3802H9.5445V11.6199H9.99741C10.154%2011.6199%2010.2852%2011.6573%2010.3868%2011.7229C10.4884%2011.7931%2010.5646%2011.8914%2010.6154%2012.0178C10.6662%2012.1489%2010.6958%2012.3081%2010.6958%2012.5C10.6958%2012.6967%2010.6662%2012.8558%2010.6154%2012.9822C10.5646%2013.1133%2010.4884%2013.2116%2010.3868%2013.2772C10.2852%2013.3474%2010.154%2013.3802%209.99741%2013.3802Z%22%20fill%3D%22%23B00020%22%2F%3E%0A%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M16.6167%207.22502C16.6413%207.29805%2016.658%207.37347%2016.6667%207.45002V15.8334C16.6667%2016.4964%2016.4033%2017.1323%2015.9345%2017.6011C15.4656%2018.07%2014.8297%2018.3334%2014.1667%2018.3334H5.83337C5.17033%2018.3334%204.53445%2018.07%204.06561%2017.6011C3.59677%2017.1323%203.33337%2016.4964%203.33337%2015.8334V4.16669C3.33337%203.50365%203.59677%202.86776%204.06561%202.39892C4.53445%201.93008%205.17033%201.66669%205.83337%201.66669H10.875C10.9719%201.67864%2011.0654%201.7098%2011.15%201.75835C11.1749%201.75482%2011.2002%201.75482%2011.225%201.75835C11.3107%201.79842%2011.3895%201.85187%2011.4584%201.91669L16.4584%206.91669C16.5232%206.98557%2016.5766%207.06434%2016.6167%207.15002V7.22502ZM13.825%206.66669L11.6667%204.50835V5.83335C11.6667%206.05437%2011.7545%206.26633%2011.9108%206.42261C12.0671%206.57889%2012.279%206.66669%2012.5%206.66669H13.825ZM14.756%2016.4226C14.9122%2016.2663%2015%2016.0544%2015%2015.8334V15H5.00004V15.8334C5.00004%2016.0544%205.08784%2016.2663%205.24412%2016.4226C5.4004%2016.5789%205.61236%2016.6667%205.83337%2016.6667H14.1667C14.3877%2016.6667%2014.5997%2016.5789%2014.756%2016.4226ZM5.00004%2010V4.16669C5.00004%203.94567%205.08784%203.73371%205.24412%203.57743C5.4004%203.42115%205.61236%203.33335%205.83337%203.33335H10V5.83335C10%206.4964%2010.2634%207.13228%2010.7323%207.60112C11.2011%208.06996%2011.837%208.33335%2012.5%208.33335H15V10H5.00004ZM7.24712%2010.8334H5.83337V14.1667H6.67993V13.1555H7.24712C7.60268%2013.1555%207.88628%2013.0525%208.09368%2012.8418C8.29686%2012.6311%208.40268%2012.3502%208.40268%2011.9944C8.40268%2011.6433%208.29686%2011.3624%208.09368%2011.1517C7.88628%2010.941%207.60268%2010.8334%207.24712%2010.8334ZM10.0143%2010.8334H8.69795V14.1667H10.0143C10.2725%2014.1667%2010.5011%2014.1292%2010.7001%2014.0496C10.899%2013.9701%2011.0641%2013.8577%2011.1911%2013.7079C11.318%2013.5581%2011.4154%2013.3849%2011.4789%2013.1789C11.5424%2012.9729%2011.5762%2012.7481%2011.5762%2012.5C11.5762%2012.2566%2011.5424%2012.0319%2011.4789%2011.8259C11.4154%2011.6199%2011.318%2011.4467%2011.1911%2011.2968C11.0641%2011.147%2010.899%2011.0347%2010.7001%2010.9551C10.5011%2010.8755%2010.2725%2010.8334%2010.0143%2010.8334ZM14.1667%2010.8334V11.6667H12.7191V12.1021H14.0736V12.8886H12.7191V14.1667H11.8725V10.8334H14.1667Z%22%20fill%3D%22%23B00020%22%2F%3E%0A%3C%2Fsvg%3E%0A");
    background-repeat: no-repeat;
    background-size: 20px 20px;
  }
  
  .accordion-icon-word-version {
    width: 20px;
    height: 20px;
    background-image: url("data:image/svg+xml,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M16.6667%207.45002C16.658%207.37347%2016.6413%207.29805%2016.6167%207.22502V7.15002C16.5766%207.06434%2016.5232%206.98557%2016.4584%206.91669L11.4584%201.91669C11.3895%201.85187%2011.3107%201.79842%2011.225%201.75835C11.2002%201.75482%2011.1749%201.75482%2011.15%201.75835C11.0654%201.7098%2010.9719%201.67864%2010.875%201.66669H5.83337C5.17033%201.66669%204.53445%201.93008%204.06561%202.39892C3.59677%202.86776%203.33337%203.50365%203.33337%204.16669V15.8334C3.33337%2016.4964%203.59677%2017.1323%204.06561%2017.6011C4.53445%2018.07%205.17033%2018.3334%205.83337%2018.3334H14.1667C14.8297%2018.3334%2015.4656%2018.07%2015.9345%2017.6011C16.4033%2017.1323%2016.6667%2016.4964%2016.6667%2015.8334V7.45002ZM11.6667%204.50835L13.825%206.66669H12.5C12.279%206.66669%2012.0671%206.57889%2011.9108%206.42261C11.7545%206.26633%2011.6667%206.05437%2011.6667%205.83335V4.50835ZM15%2015.8334C15%2016.0544%2014.9122%2016.2663%2014.756%2016.4226C14.5997%2016.5789%2014.3877%2016.6667%2014.1667%2016.6667H5.83337C5.61236%2016.6667%205.4004%2016.5789%205.24412%2016.4226C5.08784%2016.2663%205.00004%2016.0544%205.00004%2015.8334V15H15V15.8334ZM5.00004%204.16669V10H15V8.33335H12.5C11.837%208.33335%2011.2011%208.06996%2010.7323%207.60112C10.2634%207.13228%2010%206.4964%2010%205.83335V3.33335H5.83337C5.61236%203.33335%205.4004%203.42115%205.24412%203.57743C5.08784%203.73371%205.00004%203.94567%205.00004%204.16669ZM11.2464%2011.7931L11.4725%2010.8334H12.5L11.5561%2014.1667H10.4548L10.2385%2013.1789C10.1303%2012.7013%2010.0517%2012.2987%2010.0025%2011.9616C9.9435%2012.3128%209.85992%2012.7201%209.76159%2013.1789L9.54527%2014.1667H8.44399L7.50004%2010.8334H8.52757L8.75373%2011.7931C8.89139%2012.4017%208.98972%2012.8465%209.04871%2013.1274C9.13229%2012.6498%209.22079%2012.2051%209.30928%2011.7931L9.51577%2010.8334H10.4843L10.6908%2011.7931C10.7793%2012.2051%2010.8629%2012.6498%2010.9514%2013.1274C11.0054%2012.8465%2011.1038%2012.4017%2011.2464%2011.7931Z%22%20fill%3D%22%23005084%22%2F%3E%0A%3C%2Fsvg%3E%0A");
    background-repeat: no-repeat;
    background-size: 20px 20px;
  }
  
  .accordion-icon-text-version {
    width: 20px;
    height: 20px;
    background-image: url("data:image/svg+xml,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cpath%20d%3D%22M7.50004%208.33329H8.33337C8.55439%208.33329%208.76635%208.2455%208.92263%208.08922C9.07891%207.93293%209.16671%207.72097%209.16671%207.49996C9.16671%207.27895%209.07891%207.06698%208.92263%206.9107C8.76635%206.75442%208.55439%206.66663%208.33337%206.66663H7.50004C7.27903%206.66663%207.06707%206.75442%206.91079%206.9107C6.7545%207.06698%206.66671%207.27895%206.66671%207.49996C6.66671%207.72097%206.7545%207.93293%206.91079%208.08922C7.06707%208.2455%207.27903%208.33329%207.50004%208.33329ZM7.50004%209.99996C7.27903%209.99996%207.06707%2010.0878%206.91079%2010.244C6.7545%2010.4003%206.66671%2010.6123%206.66671%2010.8333C6.66671%2011.0543%206.7545%2011.2663%206.91079%2011.4225C7.06707%2011.5788%207.27903%2011.6666%207.50004%2011.6666H12.5C12.7211%2011.6666%2012.933%2011.5788%2013.0893%2011.4225C13.2456%2011.2663%2013.3334%2011.0543%2013.3334%2010.8333C13.3334%2010.6123%2013.2456%2010.4003%2013.0893%2010.244C12.933%2010.0878%2012.7211%209.99996%2012.5%209.99996H7.50004ZM16.6667%207.44996C16.658%207.3734%2016.6413%207.29799%2016.6167%207.22496V7.14996C16.5766%207.06428%2016.5232%206.98551%2016.4584%206.91663L11.4584%201.91663C11.3895%201.85181%2011.3107%201.79836%2011.225%201.75829C11.2002%201.75476%2011.1749%201.75476%2011.15%201.75829C11.0654%201.70974%2010.9719%201.67858%2010.875%201.66663H5.83337C5.17033%201.66663%204.53445%201.93002%204.06561%202.39886C3.59677%202.8677%203.33337%203.50358%203.33337%204.16663V15.8333C3.33337%2016.4963%203.59677%2017.1322%204.06561%2017.6011C4.53445%2018.0699%205.17033%2018.3333%205.83337%2018.3333H14.1667C14.8297%2018.3333%2015.4656%2018.0699%2015.9345%2017.6011C16.4033%2017.1322%2016.6667%2016.4963%2016.6667%2015.8333V7.49996C16.6667%207.49996%2016.6667%207.49996%2016.6667%207.44996ZM11.6667%204.50829L13.825%206.66663H12.5C12.279%206.66663%2012.0671%206.57883%2011.9108%206.42255C11.7545%206.26627%2011.6667%206.05431%2011.6667%205.83329V4.50829ZM15%2015.8333C15%2016.0543%2014.9122%2016.2663%2014.756%2016.4225C14.5997%2016.5788%2014.3877%2016.6666%2014.1667%2016.6666H5.83337C5.61236%2016.6666%205.4004%2016.5788%205.24412%2016.4225C5.08784%2016.2663%205.00004%2016.0543%205.00004%2015.8333V4.16663C5.00004%203.94561%205.08784%203.73365%205.24412%203.57737C5.4004%203.42109%205.61236%203.33329%205.83337%203.33329H10V5.83329C10%206.49633%2010.2634%207.13222%2010.7323%207.60106C11.2011%208.0699%2011.837%208.33329%2012.5%208.33329H15V15.8333ZM12.5%2013.3333H7.50004C7.27903%2013.3333%207.06707%2013.4211%206.91079%2013.5774C6.7545%2013.7337%206.66671%2013.9456%206.66671%2014.1666C6.66671%2014.3876%206.7545%2014.5996%206.91079%2014.7559C7.06707%2014.9122%207.27903%2015%207.50004%2015H12.5C12.7211%2015%2012.933%2014.9122%2013.0893%2014.7559C13.2456%2014.5996%2013.3334%2014.3876%2013.3334%2014.1666C13.3334%2013.9456%2013.2456%2013.7337%2013.0893%2013.5774C12.933%2013.4211%2012.7211%2013.3333%2012.5%2013.3333Z%22%20fill%3D%22%234E5A5F%22%2F%3E%0A%3C%2Fsvg%3E%0A");
    background-repeat: no-repeat;
    background-size: 20px 20px;
  }
 
  .accordion-detail-version {
    display: flex;
    align-items: center;
    text-align: start;
    ${text_xs};
    color: #4E5A5F;
    ${({ theme }) => spacing({ theme, $mt: 1, $mb: 0 })}
    ${mq.lessThan('sm')}{
      flex-wrap: wrap;
    }
  }
`;

export const externalLinkStyle = () => css`
  color: inherit;
  padding-right: 24px;
  box-shadow: none !important;
  border-bottom: 2px solid #2db84b;
  text-decoration: none;
  font-size: inherit;
  line-height: inherit;
  background-image: url('data:image/svg+xml,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cpath%20d%3D%22M14.9999%2012.35C14.7789%2012.35%2014.5669%2012.4378%2014.4107%2012.5941C14.2544%2012.7504%2014.1666%2012.9623%2014.1666%2013.1833V14.1667C14.1666%2014.3877%2014.0788%2014.5997%2013.9225%2014.7559C13.7662%2014.9122%2013.5543%2015%2013.3333%2015H5.83325C5.61224%2015%205.40028%2014.9122%205.244%2014.7559C5.08772%2014.5997%204.99992%2014.3877%204.99992%2014.1667V6.66668C4.99992%206.44566%205.08772%206.2337%205.244%206.07742C5.40028%205.92114%205.61224%205.83334%205.83325%205.83334H6.81659C7.0376%205.83334%207.24956%205.74555%207.40584%205.58927C7.56212%205.43299%207.64992%205.22102%207.64992%205.00001C7.64992%204.779%207.56212%204.56703%207.40584%204.41075C7.24956%204.25447%207.0376%204.16668%206.81659%204.16668H5.83325C5.17021%204.16668%204.53433%204.43007%204.06549%204.89891C3.59664%205.36775%203.33325%206.00364%203.33325%206.66668V14.1667C3.33325%2014.8297%203.59664%2015.4656%204.06549%2015.9344C4.53433%2016.4033%205.17021%2016.6667%205.83325%2016.6667H13.3333C13.9963%2016.6667%2014.6322%2016.4033%2015.101%2015.9344C15.5699%2015.4656%2015.8333%2014.8297%2015.8333%2014.1667V13.1833C15.8333%2012.9623%2015.7455%2012.7504%2015.5892%2012.5941C15.4329%2012.4378%2015.2209%2012.35%2014.9999%2012.35ZM16.5999%203.85001C16.5154%203.64639%2016.3535%203.48457%2016.1499%203.40001C16.0497%203.35731%2015.9421%203.33466%2015.8333%203.33334H10.8333C10.6122%203.33334%2010.4003%203.42114%2010.244%203.57742C10.0877%203.7337%209.99992%203.94566%209.99992%204.16668C9.99992%204.38769%2010.0877%204.59965%2010.244%204.75593C10.4003%204.91221%2010.6122%205.00001%2010.8333%205.00001H13.8249L8.57492%2010.2417C8.49681%2010.3191%208.43482%2010.4113%208.39251%2010.5129C8.3502%2010.6144%208.32842%2010.7233%208.32842%2010.8333C8.32842%2010.9434%208.3502%2011.0523%208.39251%2011.1538C8.43482%2011.2554%208.49681%2011.3475%208.57492%2011.425C8.65239%2011.5031%208.74455%2011.5651%208.8461%2011.6074C8.94765%2011.6497%209.05658%2011.6715%209.16658%2011.6715C9.2766%2011.6715%209.38552%2011.6497%209.48707%2011.6074C9.58861%2011.5651%209.68078%2011.5031%209.75825%2011.425L14.9999%206.17501V9.16668C14.9999%209.38769%2015.0877%209.59965%2015.244%209.75593C15.4003%209.91221%2015.6122%2010%2015.8333%2010C16.0543%2010%2016.2662%209.91221%2016.4225%209.75593C16.5788%209.59965%2016.6666%209.38769%2016.6666%209.16668V4.16668C16.6653%204.05778%2016.6426%203.9502%2016.5999%203.85001Z%22%20fill%3D%22%23188838%22%2F%3E%0A%3C%2Fsvg%3E%0A');
  background-repeat: no-repeat;
  background-position: right center;
  background-size: 20px;

  ${mq.lessThan('md')} {
    background-size: 18px;
  }

  &:hover,
  &:focus {
    background-color: #2db84b;
    color: #041c2c !important;
    background-image: url('data:image/svg+xml,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cpath%20d%3D%22M14.9999%2012.35C14.7789%2012.35%2014.5669%2012.4378%2014.4107%2012.5941C14.2544%2012.7504%2014.1666%2012.9623%2014.1666%2013.1833V14.1667C14.1666%2014.3877%2014.0788%2014.5997%2013.9225%2014.7559C13.7662%2014.9122%2013.5543%2015%2013.3333%2015H5.83325C5.61224%2015%205.40028%2014.9122%205.244%2014.7559C5.08772%2014.5997%204.99992%2014.3877%204.99992%2014.1667V6.66668C4.99992%206.44566%205.08772%206.2337%205.244%206.07742C5.40028%205.92114%205.61224%205.83334%205.83325%205.83334H6.81659C7.0376%205.83334%207.24956%205.74555%207.40584%205.58927C7.56212%205.43299%207.64992%205.22102%207.64992%205.00001C7.64992%204.779%207.56212%204.56703%207.40584%204.41075C7.24956%204.25447%207.0376%204.16668%206.81659%204.16668H5.83325C5.17021%204.16668%204.53433%204.43007%204.06549%204.89891C3.59664%205.36775%203.33325%206.00364%203.33325%206.66668V14.1667C3.33325%2014.8297%203.59664%2015.4656%204.06549%2015.9344C4.53433%2016.4033%205.17021%2016.6667%205.83325%2016.6667H13.3333C13.9963%2016.6667%2014.6322%2016.4033%2015.101%2015.9344C15.5699%2015.4656%2015.8333%2014.8297%2015.8333%2014.1667V13.1833C15.8333%2012.9623%2015.7455%2012.7504%2015.5892%2012.5941C15.4329%2012.4378%2015.2209%2012.35%2014.9999%2012.35ZM16.5999%203.85001C16.5154%203.64639%2016.3535%203.48457%2016.1499%203.40001C16.0497%203.35731%2015.9421%203.33466%2015.8333%203.33334H10.8333C10.6122%203.33334%2010.4003%203.42114%2010.244%203.57742C10.0877%203.7337%209.99992%203.94566%209.99992%204.16668C9.99992%204.38769%2010.0877%204.59965%2010.244%204.75593C10.4003%204.91221%2010.6122%205.00001%2010.8333%205.00001H13.8249L8.57492%2010.2417C8.49681%2010.3191%208.43482%2010.4113%208.39251%2010.5129C8.3502%2010.6144%208.32842%2010.7233%208.32842%2010.8333C8.32842%2010.9434%208.3502%2011.0523%208.39251%2011.1538C8.43482%2011.2554%208.49681%2011.3475%208.57492%2011.425C8.65239%2011.5031%208.74455%2011.5651%208.8461%2011.6074C8.94765%2011.6497%209.05658%2011.6715%209.16658%2011.6715C9.2766%2011.6715%209.38552%2011.6497%209.48707%2011.6074C9.58861%2011.5651%209.68078%2011.5031%209.75825%2011.425L14.9999%206.17501V9.16668C14.9999%209.38769%2015.0877%209.59965%2015.244%209.75593C15.4003%209.91221%2015.6122%2010%2015.8333%2010C16.0543%2010%2016.2662%209.91221%2016.4225%209.75593C16.5788%209.59965%2016.6666%209.38769%2016.6666%209.16668V4.16668C16.6653%204.05778%2016.6426%203.9502%2016.5999%203.85001Z%22%20fill%3D%22%23041C2C%22%2F%3E%0A%3C%2Fsvg%3E%0A');
  }
`;

export const buttonExternalLinkStyle = ({ theme }) => css`
  ${heading_s};
  text-decoration: none;
  background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAACfSURBVHgB7ZTdDYAgDIQbJ3CEbqob6IaMoBtUSDQaQu3x86AJl/ACd/cBCRB1/U4i4kSXi/2DUjL5sV0hP0bCxKbDly3xrjSAn+f4RAjg2jkbvme5ywGYxkQ5NwOkypEcBNDKmwDeys/1MOdqAGp5lgxAXfkbwMiMgj7GQgB2/xUANZP6i/YzxISV8zOHBGYp00qoglnuP8lS8M3U9Vkd7fHRcz+fJTEAAAAASUVORK5CYII=');
  background-repeat: no-repeat;
  background-position: right 16px center;
  background-color: #188838 !important;
  background-size: 24px;
  cursor: pointer;
  display: inline-block;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 28px;
  color: #fff !important;
  box-shadow: none !important;
  padding-right: 43px;
  ${spacing({
  theme,
  $pl: 2,
  $py: '8px'
})}

  ${mq.lessThan('md')} {
  background-size: 20px;
  background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAADGSURBVHgB7VJRDcIwEL2bAiQUB0jAAUiogyEBHOBgxQFIQAE4GDiYg/KaHElT2mtJ+GMveblc++7tXjqivwO3Cr33Pcoqc3Vh5rM6CN7AEdxF56HPYYjnu8TMohxBI0fT+w5bLFmA1lFjLCdfNYpmEI3LbZg1bDGTXo9c2T4MWvCE1LakazJUzB7gUxv8iIx2H8esoWXDO3jQYhZRe5RE28tv9jPDMTA97wpiQ7rZAiVwoopw7b+DTT04Y7pF2VAdVzyUoxkv833pEwFLO5YAAAAASUVORK5CYII=');
  ${spacing({
  theme,
  $pl: 2,
  $py: '10px'
})}

  }

  &:hover,
  &:focus {
    background-color: #005a0b !important;
  }
`;

export const externalStyle = ({ theme }) => css`
  .button-external-link {
    ${buttonExternalLinkStyle}
  }

  .call-to-action-link {
    ${buttonExternalLinkStyle}
    background-image: none;
    padding: 6px 16px;
  }

  .external-link {
    ${externalLinkStyle}
  }
`;

export const sectionWithImageStyle = () => css`
  .section-with-image-container {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    ${mq.lessThan('md')} {
      flex-direction: column;
      align-items: start;
    }
  }

  .section-with-image-content {
    width: 60%;
    ${mq.lessThan('md')} {
      width: 100%;
      margin-bottom: 40px;
    }
  }

  .section-with-image-image {
    width: 40%;

    ${mq.lessThan('md')} {
      width: 50%;
    }

    img {
      width: 100% !important;
      height: auto !important;
    }
  }
`;

export const RichTextStyle = css`
  ${baseStyles}
  ${blockStyle}
  ${headingStyle}
  ${textStyle}
  ${linkStyle}
  ${calloutStyle}
  ${tooltipStyle}
  ${buttonContainerStyle}
  ${listStyle}
  ${tableStyle}
  ${blockquoteStyle}
  ${responsiveEmbedStyle}
  ${externalStyle}
  ${sectionWithImageStyle}
  ${accordionStyles}
`;

export default styled(RichText)`
  ${RichTextStyle}
`;
